// ============================================================================
// FUNCTIONS.JS - Cloud Functions Client Wrapper
// 
// Provides typed wrappers for calling Cloud Functions from the client
// ============================================================================

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirebaseApp } from './store.firebase.js';

let functions = null;

const getFunctionsInstance = () => {
  if (!functions) {
    const app = getFirebaseApp();
    if (app) {
      functions = getFunctions(app);
    }
  }
  return functions;
};

// ============================================================================
// createUser
// 
// Creates a new user (Auth + Firestore)
// Only callable by platform_admin
// 
// @param {Object} params
// @param {string} params.email - User email
// @param {string} params.name - User display name
// @param {string} params.role - User role
// @param {string|null} params.tenantId - Tenant ID (required for non-platform roles)
// @param {string[]} params.specialtyIds - Specialty IDs (required for specialists)
// @param {string} [params.tempPassword] - Optional temp password
// 
// @returns {Promise<{success: boolean, uid: string, email: string, tempPassword: string}>}
// ============================================================================

export const createUser = async ({ email, name, role, tenantId, specialtyIds, tempPassword }) => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }
  
  const callable = httpsCallable(fn, 'createUser');
  const result = await callable({ email, name, role, tenantId, specialtyIds, tempPassword });
  return result.data;
};

// ============================================================================
// updateUserStatus
// 
// Updates a user's status (active/disabled)
// Callable by platform_admin (any user) or tenant_admin (own tenant)
// 
// @param {string} userId - User ID to update
// @param {string} status - New status ('active' or 'disabled')
// 
// @returns {Promise<{success: boolean}>}
// ============================================================================

export const updateUserStatusCF = async (userId, status) => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }
  
  const callable = httpsCallable(fn, 'updateUserStatus');
  const result = await callable({ userId, status });
  return result.data;
};

// ============================================================================
// updateUserClaims
//
// Updates a user's role and/or specialtyIds, syncing both Firestore doc
// and Firebase Auth JWT custom claims.
// Callable by platform_admin or tenant_admin (own tenant).
//
// @param {string} userId - User ID to update
// @param {string} [role] - New role
// @param {string[]} [specialtyIds] - New specialty assignments
//
// @returns {Promise<{success: boolean}>}
// ============================================================================

export const updateUserClaimsCF = async (userId, { role, specialtyIds } = {}) => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }

  const callable = httpsCallable(fn, 'updateUserClaims');
  const result = await callable({ userId, role, specialtyIds });
  return result.data;
};

// ============================================================================
// deleteUser
//
// Deletes a user (Auth + Firestore)
// Only callable by platform_admin
//
// @param {string} userId - User ID to delete
//
// @returns {Promise<{success: boolean}>}
// ============================================================================

export const deleteUserCF = async (userId) => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }

  const callable = httpsCallable(fn, 'deleteUser');
  const result = await callable({ userId });
  return result.data;
};

// ============================================================================
// claimInviteCF
//
// Claims an email invite and provisions the caller's account.
// Delegates all writes to the claimInvite Cloud Function (Admin SDK).
//
// @param {string} inviteId - The invite document ID to claim
//
// @returns {Promise<{tenantId: string, role: string}>}
// ============================================================================

export const claimInviteCF = async (inviteId) => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }

  const callable = httpsCallable(fn, 'claimInvite');
  const result = await callable({ inviteId });
  return result.data; // { tenantId, role }
};

// ============================================================================
// selfHealIsActiveCF
//
// Calls the selfHealIsActive Cloud Function to stamp isActive=true on the
// caller's user document when it was bootstrapped without that field.
// Called automatically by ensureUserProfile on PERMISSION_DENIED.
//
// @returns {Promise<{healed: boolean, reason?: string}>}
// ============================================================================

export const selfHealIsActiveCF = async () => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }
  const callable = httpsCallable(fn, 'selfHealIsActive');
  const result = await callable({});
  return result.data;
};

export const backfillNotYetConversionsCF = async () => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }
  const callable = httpsCallable(fn, 'backfillNotYetConversions');
  const result = await callable({});
  return result.data;
};

export const selfHealClaimsCF = async () => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }
  const callable = httpsCallable(fn, 'selfHealClaims');
  const result = await callable({});
  return result.data;
};

export const checkEmailExistsCF = async (email) => {
  const fn = getFunctionsInstance();
  if (!fn) {
    throw new Error('Firebase Functions not initialized');
  }
  const callable = httpsCallable(fn, 'checkEmailExists');
  const result = await callable({ email });
  return result.data;
};

// ============================================================================
// Queue Pings — broadcast an operational fire to a sector role (server-created)
// ============================================================================

export const createQueuePingCF = async ({ source, purpose, note }) => {
  const fn = getFunctionsInstance();
  if (!fn) throw new Error('Firebase Functions not initialized');
  const callable = httpsCallable(fn, 'createQueuePing');
  const result = await callable({ source, purpose, note });
  return result.data; // { success, queuePingId, recipientCount }
};

export const dismissQueuePingCF = async (queuePingId) => {
  const fn = getFunctionsInstance();
  if (!fn) throw new Error('Firebase Functions not initialized');
  const callable = httpsCallable(fn, 'dismissQueuePing');
  const result = await callable({ queuePingId });
  return result.data; // { success }
};

export default {
  createUser,
  updateUserStatusCF,
  deleteUserCF,
  claimInviteCF,
  selfHealIsActiveCF,
  selfHealClaimsCF,
  backfillNotYetConversionsCF,
  checkEmailExistsCF,
  createQueuePingCF,
  dismissQueuePingCF,
};
